import {
  assetFetchClient,
  cryptoCurrencyDataSource,
} from '@/constants/datasource'
import { evmChains } from '@/constants/evmChain'
import { polkadotChains } from '@/constants/polkadotChain'
import { evmTokenApiEndpoint, priceDataSourceApiEndpoint } from '@/environment'
import type { RunToDatabase } from '@/types'
import { type EvmConverterWorker, createEvmConverterWorker } from '@/workers'
import { createConverterByWorker } from '@/workers/evmConverterHelper'
import type { FiatCurrency } from '@0xtorch/core'
import {
  analyzeCardanoAccounts,
  analyzeEvmAccounts,
  analyzeEvmTransaction,
  analyzeExchangeAccounts,
  analyzeExchangeApiAccounts,
  analyzePolkadotAccounts,
  analyzeSolanaAccounts,
  analyzeSolanaTransaction,
  analyzeXrplAccounts,
  fetchEvmTransactions,
  fetchSolanaAddressData,
  fetchSolanaTransactions,
  saveKucoinApiData,
} from '@pkg/analyze'
import { accountCategoryList, isAvailableApiCategory } from '@pkg/basic'
import { createBlockfrostClient } from '@pkg/cardano'
import type {
  Account,
  AccountEvm,
  AccountExchange,
  AccountExchangeApi,
  AccountPolkadot,
  AccountSolana,
} from '@pkg/database-portfolio'
import {
  selectAccountExchangeApis,
  selectAccounts,
  selectCryptoCurrencies,
  selectPortfolio,
} from '@pkg/database-portfolio'
import { getBlockDatas, parseExtrinsicToActions } from '@pkg/polkadot'
import { convertSolanaTransactionToProgramActions } from '@pkg/solana-converter'
import { createXrplRpcClient } from '@pkg/xrpl'
import { wrap } from 'comlink'
import type { Setter } from 'jotai'
import {
  saveAnalyzedEvmChainTransactionProgressStatus,
  saveEndEvmChainAnalyzeProgressStatus,
  saveFetchedEvmChainAccountDataProgressStatus,
  saveFetchedEvmChainTransactionProgressStatus,
  saveStartAnalyzeEvmTransactionListProgressStatus,
  saveStartEvmChainAnalyzeProgressStatus,
} from '../states/evm'
import {
  saveCalledExchangeApiProgressStatus,
  saveEndExchangeAnalyzeProgressStatus,
  saveFetchedExchangeApiCallSizeProgressStatus,
  saveFetchedExchangeApiProgressStatus,
  saveStartExchangeAnalyzeProgressStatus,
} from '../states/exchange'
import {
  saveAnalyzedPolkadotChainBlockProgressStatus,
  saveEndPolkadotChainAnalyzeProgressStatus,
  saveFetchedPolkadotChainAccountDataProgressStatus,
  saveFetchedPolkadotChainBlockProgressStatus,
  saveStartAnalyzePolkadotBlocksProgressStatus,
  saveStartPolkadotChainAnalyzeProgressStatus,
} from '../states/polkadot'
import { exchangeApiProgressesAtom, progressesAtom } from '../states/progress'
import {
  saveAnalyzedSolanaTransactionProgressStatus,
  saveEndSolanaAnalyzeProgressStatus,
  saveFetchedSolanaAccountDataProgressStatus,
  saveFetchedSolanaTransactionProgressStatus,
  saveStartAnalyzeSolanaTransactionListProgressStatus,
  saveStartSolanaAnalyzeProgressStatus,
} from '../states/solana'
import type { ExchangeApiProgress } from '../types'

type AnalyzeAccountListParameters = {
  readFromDatabase: RunToDatabase
  writeToDatabase: RunToDatabase
  getToken?: () => Promise<string | null>
  readonly targetAccountIdList: readonly number[] | undefined
  readonly fiatCurrencyList: readonly FiatCurrency[]
  readonly apiEndpoint: string
  readonly datasourceApiEndpoint: string
  readonly proxyApiEndpoint: string
  readonly splTokenApiEndpoint: string
  readonly solanaRpcEndpoints: readonly string[]
  readonly skipPolkadotIndexFetch?: boolean
  readonly beforePolkadotAnalyze?: () => Promise<void>
  readonly set: Setter
  onError: (error: unknown) => void
}

export const analyzeAccountList = async ({
  readFromDatabase,
  writeToDatabase,
  getToken,
  targetAccountIdList,
  fiatCurrencyList,
  apiEndpoint,
  datasourceApiEndpoint,
  proxyApiEndpoint,
  splTokenApiEndpoint,
  solanaRpcEndpoints,
  skipPolkadotIndexFetch = false,
  beforePolkadotAnalyze,
  set,
  onError,
}: AnalyzeAccountListParameters) => {
  // DB から account list , portfolio, crypto currency list 取得
  const [portfolio, accountList, accountExchangeApiList, cryptoCurrencyList] =
    await Promise.all([
      readFromDatabase((database) => selectPortfolio({ database })),
      readFromDatabase((database) => selectAccounts({ database })),
      readFromDatabase((database) => selectAccountExchangeApis({ database })),
      readFromDatabase((database) =>
        selectCryptoCurrencies({
          database,
        }),
      ),
    ])
  if (portfolio === undefined) {
    throw new Error('portfolio not found')
  }
  const fiatCurrency = fiatCurrencyList.find(({ id }) => id === portfolio.fiat)
  if (fiatCurrency === undefined) {
    throw new Error(`fiat currency not found: ${portfolio.fiat}`)
  }
  const startTime = portfolio.startTime?.getTime() ?? 0

  // target account id に合致する account を category 毎にまとめる / undefined の場合は全アカウント対象
  const targetAccountIdSet = new Set(targetAccountIdList)
  const targetAccountList =
    targetAccountIdList === undefined
      ? accountList
      : accountList.filter((account) => targetAccountIdSet.has(account.id))
  const {
    evmChainAccountList,
    exchangeCategoryAccountList,
    polkadotChainAccountList,
    solanaAccountList,
  } = groupAccountListByCategory(targetAccountList, accountExchangeApiList)

  // worker 作成
  const evmConverterWorker =
    evmChainAccountList.length > 0 ? createEvmConverterWorker() : undefined

  try {
    // category 毎に account action 生成・保存
    await Promise.all([
      getToken === undefined
        ? undefined
        : analyzeXrplAccounts({
            cryptoCurrencies: cryptoCurrencyList,
            readFromDatabase,
            writeToDatabase,
            startTime,
            accounts: accountList.filter((account) => account.type === 'xrpl'),
            targetAccountIds: new Set(
              targetAccountList.map((account) => account.id),
            ),
            client: createXrplRpcClient({
              proxyEndpoint: proxyApiEndpoint,
              getToken,
            }),
            fiat: fiatCurrency,
            priceDataSource: cryptoCurrencyDataSource,
            onError,
            onProgress: (progress) =>
              set(progressesAtom, (current) => [
                ...current.filter((item) => item.category !== 'xrpl'),
                ...(progress === undefined
                  ? []
                  : [{ category: 'xrpl', ...progress }]),
              ]),
          }),
      getToken === undefined
        ? undefined
        : analyzeCardanoAccounts({
            readFromDatabase,
            writeToDatabase,
            startTime,
            accounts: accountList.filter(
              (account) => account.type === 'cardano',
            ),
            targetAccountIds: new Set(
              targetAccountList.map((account) => account.id),
            ),
            client: createBlockfrostClient({
              proxyEndpoint: proxyApiEndpoint,
              getToken,
            }),
            cryptoCurrencies: cryptoCurrencyList,
            fiat: fiatCurrency,
            priceDataSource: cryptoCurrencyDataSource,
            onError,
            onProgress: (progress) =>
              set(progressesAtom, (current) => [
                ...current.filter((item) => item.category !== 'cardano'),
                ...(progress === undefined
                  ? []
                  : [{ category: 'cardano', ...progress }]),
              ]),
          }),
      ...evmChainAccountList.map((evmChainAccount) => {
        const chain = evmChains.find(({ id }) => id === evmChainAccount.chainId)
        if (chain === undefined) {
          return
        }
        if (evmConverterWorker === undefined) {
          return
        }
        const { converter } = wrap<EvmConverterWorker>(evmConverterWorker)
        return analyzeEvmAccounts({
          readFromDatabase,
          writeToDatabase,
          getToken,
          startTime,
          chain,
          accounts: evmChainAccount.accountList,
          allEvmAccountAddressList: accountList
            .filter(
              (account): account is AccountEvm =>
                account.type === 'evm' &&
                account.evmChainId === evmChainAccount.chainId,
            )
            .map((account) => account.evmAddress),
          fiatCurrency,
          cryptoCurrencyDataSource,
          assetFetchClient,
          apiEndpoint,
          datasourceApiEndpoint,
          evmTokenApiEndpoint,
          fetchTransactions: fetchEvmTransactions,
          analyzeTransaction: analyzeEvmTransaction,
          converter: createConverterByWorker(converter),
          onStart: (accountIdSet) => {
            saveStartEvmChainAnalyzeProgressStatus({
              chain,
              accountIdSet,
              set,
            })
          },
          onEnd: () => {
            saveEndEvmChainAnalyzeProgressStatus({
              chain,
              set,
            })
          },
          onFetchedAccount: (accountId) => {
            saveFetchedEvmChainAccountDataProgressStatus({
              chain,
              accountId,
              set,
            })
          },
          onStartAnalyzeTransaction: (transactionHashSet) => {
            saveStartAnalyzeEvmTransactionListProgressStatus({
              chain,
              transactionHashSet,
              set,
            })
          },
          onFetchedTransaction: (transactionHash) => {
            saveFetchedEvmChainTransactionProgressStatus({
              chain,
              transactionHash,
              set,
            })
          },
          onAnalyzedTransaction: (transactionHash) => {
            saveAnalyzedEvmChainTransactionProgressStatus({
              chain,
              transactionHash,
              set,
            })
          },
          onError,
        })
      }),
      ...exchangeCategoryAccountList.map(async (exchangeCategoryAccount) => {
        if (isAvailableApiCategory(exchangeCategoryAccount.category)) {
          try {
            return await analyzeExchangeApiAccounts({
              accountIds: exchangeCategoryAccount.accountList.map(
                ({ id }) => id,
              ),
              assetFetchClient,
              category: exchangeCategoryAccount.category,
              cryptoCurrencyApiEndpoint: priceDataSourceApiEndpoint,
              cryptoCurrencyDataSource,
              fiat: fiatCurrency,
              fiats: [...fiatCurrencyList],
              portfolioStartTime: startTime,
              proxy: proxyApiEndpoint,
              getToken,
              onError,
              onUpdateProgress: (change) => {
                set(exchangeApiProgressesAtom, (prev) => {
                  const current: ExchangeApiProgress = {
                    category: exchangeCategoryAccount.category,
                    size: 0,
                    done: 0,
                    type: '',
                    subType: undefined,
                    range: 0,
                    synced: 0,
                    ...prev[exchangeCategoryAccount.category],
                  }
                  if (change.done !== undefined) {
                    current.done = change.done
                  }
                  if (change.key !== undefined) {
                    current.type = change.key.type
                    current.subType = change.key.subType
                  }
                  if (change.range !== undefined) {
                    current.range = change.range
                  }
                  if (change.size !== undefined) {
                    current.size = change.size
                  }
                  if (change.synced !== undefined) {
                    current.synced = change.synced ?? 0
                  }
                  return {
                    ...prev,
                    [exchangeCategoryAccount.category]: current,
                  }
                })
              },
              readFromDatabase,
              writeToDatabase,
            })
          } finally {
            set(exchangeApiProgressesAtom, (prev) => ({
              ...prev,
              [exchangeCategoryAccount.category]: undefined,
            }))
          }
        }
        const category = accountCategoryList.find(
          ({ id }) => id === exchangeCategoryAccount.category,
        )
        if (
          category === undefined ||
          category.type !== 'exchange' ||
          category.exchangeApis === undefined ||
          category.exchangeApis.length === 0
        ) {
          return undefined
        }
        const onStart = (
          accountApiIdSetList: {
            readonly accountId: number
            readonly apiIdSet: Set<string>
          }[],
        ) => {
          saveStartExchangeAnalyzeProgressStatus({
            category: category.id,
            accountApiIdSetList,
            set,
          })
        }
        const onEnd = () => {
          saveEndExchangeAnalyzeProgressStatus({
            category: category.id,
            set,
          })
        }
        const onFetchedCallSize = (values: {
          readonly accountId: number
          readonly apiId: string
          readonly size: number
        }) => {
          saveFetchedExchangeApiCallSizeProgressStatus({
            category: category.id,
            ...values,
            set,
          })
        }
        const onCalled = (values: {
          readonly accountId: number
          readonly apiId: string
        }) => {
          saveCalledExchangeApiProgressStatus({
            category: category.id,
            ...values,
            set,
          })
        }
        const onFetchedApi = (values: {
          readonly accountId: number
          readonly apiId: string
        }) => {
          saveFetchedExchangeApiProgressStatus({
            category: category.id,
            ...values,
            set,
          })
        }
        switch (category.id) {
          case 'kucoin': {
            return analyzeExchangeAccounts({
              readFromDatabase,
              writeToDatabase,
              getToken,
              startTime,
              category: exchangeCategoryAccount.category,
              apiIdList: category.exchangeApis,
              accountList: exchangeCategoryAccount.accountList,
              cryptoCurrencyList,
              fiatCurrency,
              fiatCurrencyList,
              cryptoCurrencyDataSource,
              proxyApiEndpoint,
              saveApiData: saveKucoinApiData,
              onStart,
              onEnd,
              onFetchedCallSize,
              onCalled,
              onFetchedApi,
              onError,
            })
          }
          case 'okx': {
            // TODO 一時的にコメントアウト
            throw new Error(`unsupported exchange api category: ${category.id}`)
            // const worker = createFetchOkxApiDataWorker()
            // return analyzeExchangeAccounts({
            //   readFromDatabase,
            //   writeToDatabase,
            //   getToken,
            //   startTime,
            //   category: exchangeCategoryAccount.category,
            //   apiIdList: category.exchangeApis,
            //   accountList: exchangeCategoryAccount.accountList,
            //   cryptoCurrencyList,
            //   fiatCurrency,
            //   fiatCurrencyList,
            //   cryptoCurrencyDataSource,
            //   proxyApiEndpoint,
            //   saveApiData: worker.fetchOkxApiData,
            //   onStart,
            //   onEnd,
            //   onFetchedCallSize,
            //   onCalled,
            //   onFetchedApi,
            // }).finally(() => {
            //   terminate(worker)
            // })
          }
          default: {
            throw new Error(`unsupported exchange api category: ${category.id}`)
          }
        }
      }),
      ...polkadotChainAccountList.map((polkadotChainAccount) => {
        const chain = polkadotChains.find(
          ({ id }) => id === polkadotChainAccount.chainId,
        )
        if (chain === undefined) {
          return
        }
        return analyzePolkadotAccounts({
          readFromDatabase,
          writeToDatabase,
          getToken,
          startTime,
          chain,
          accounts: polkadotChainAccount.accounts,
          fiat: fiatCurrency,
          cryptoCurrencyDataSource,
          getPolkadotBlockDatas: getBlockDatas,
          parsePolkadotExtrinsicToActions: (args) =>
            Promise.resolve(parseExtrinsicToActions(args)),
          onStart: (accountIdSet) => {
            saveStartPolkadotChainAnalyzeProgressStatus({
              chain,
              accountIdSet,
              set,
            })
          },
          onEnd: () => {
            saveEndPolkadotChainAnalyzeProgressStatus({
              chain,
              set,
            })
          },
          onFetchedAccount: (accountId) => {
            saveFetchedPolkadotChainAccountDataProgressStatus({
              chain,
              accountId,
              set,
            })
          },
          onStartAnalyzeBlock: (blockNumberSet) => {
            saveStartAnalyzePolkadotBlocksProgressStatus({
              chain,
              blockNumberSet,
              set,
            })
          },
          onFetchedBlock: (blockNumber) => {
            saveFetchedPolkadotChainBlockProgressStatus({
              chain,
              blockNumber,
              set,
            })
          },
          onAnalyzedBlock: (blockNumber) => {
            saveAnalyzedPolkadotChainBlockProgressStatus({
              chain,
              blockNumber,
              set,
            })
          },
          onError,
          skipIndexFetch: skipPolkadotIndexFetch,
          beforeAnalyze: beforePolkadotAnalyze,
        })
      }),
      solanaAccountList.length > 0
        ? analyzeSolanaAccounts({
            readFromDatabase,
            writeToDatabase,
            getToken,
            startTime,
            accountList: solanaAccountList,
            allSolanaAccountAddresses: accountList
              .filter((account) => account.type === 'solana')
              .map((account) => account.solanaAddress),
            cryptoCurrencyList,
            fiatCurrency,
            cryptoCurrencyDataSource,
            assetFetchClient,
            apiEndpoint,
            rpcEndpoints: solanaRpcEndpoints,
            evmTokenApiEndpoint,
            splTokenApiEndpoint,
            fetchAddressData: fetchSolanaAddressData,
            fetchTransactions: fetchSolanaTransactions,
            analyzeTransaction: analyzeSolanaTransaction,
            converter: convertSolanaTransactionToProgramActions,
            onStart: (accountIdSet) => {
              saveStartSolanaAnalyzeProgressStatus({
                accountIdSet,
                set,
              })
            },
            onEnd: () => {
              saveEndSolanaAnalyzeProgressStatus({
                set,
              })
            },
            onFetchedAccount: (accountId) => {
              saveFetchedSolanaAccountDataProgressStatus({
                accountId,
                set,
              })
            },
            onStartAnalyzeTransaction: (transactionSignatureSet) => {
              saveStartAnalyzeSolanaTransactionListProgressStatus({
                transactionSignatureSet,
                set,
              })
            },
            onFetchedTransaction: (transactionSignature) => {
              saveFetchedSolanaTransactionProgressStatus({
                transactionSignature,
                set,
              })
            },
            onAnalyzedTransaction: (transactionSignature) => {
              saveAnalyzedSolanaTransactionProgressStatus({
                transactionSignature,
                set,
              })
            },
            onError,
          })
        : undefined,
    ])
  } finally {
    evmConverterWorker?.terminate()
  }
}

const groupAccountListByCategory = (
  accountList: readonly Account[],
  accountExchangeApiList: readonly AccountExchangeApi[],
): {
  readonly evmChainAccountList: readonly {
    readonly chainId: number
    readonly accountList: readonly AccountEvm[]
  }[]
  readonly exchangeCategoryAccountList: readonly {
    readonly category: string
    readonly accountList: readonly (AccountExchange & {
      readonly apiList: readonly AccountExchangeApi[]
    })[]
  }[]
  polkadotChainAccountList: readonly {
    chainId: number
    accounts: readonly AccountPolkadot[]
  }[]
  readonly solanaAccountList: readonly AccountSolana[]
} => {
  const evmChainAccountsMap = new Map<number, AccountEvm[]>()
  const exchangeCategoryAccountsMap = new Map<
    string,
    (AccountExchange & {
      readonly apiList: readonly AccountExchangeApi[]
    })[]
  >()
  const polkadotChainAccountsMap = new Map<number, AccountPolkadot[]>()
  const solanaAccountList: AccountSolana[] = []

  for (const account of accountList) {
    switch (account.type) {
      case 'evm': {
        const accounts = evmChainAccountsMap.get(account.evmChainId) ?? []
        accounts.push(account)
        evmChainAccountsMap.set(account.evmChainId, accounts)
        break
      }
      case 'exchange': {
        if (
          account.exchangeApiKey === undefined ||
          account.exchangeApiKey.length === 0
        ) {
          break
        }
        const accounts = exchangeCategoryAccountsMap.get(account.category) ?? []
        accounts.push({
          ...account,
          apiList: accountExchangeApiList.filter(
            (api) => api.accountId === account.id,
          ),
        })
        exchangeCategoryAccountsMap.set(account.category, accounts)
        break
      }
      case 'polkadot': {
        const accounts =
          polkadotChainAccountsMap.get(account.polkadotChainId) ?? []
        accounts.push(account)
        polkadotChainAccountsMap.set(account.polkadotChainId, accounts)
        break
      }
      case 'service': {
        break
      }
      case 'xrpl':
      case 'cardano': {
        break
      }
      case 'solana': {
        solanaAccountList.push(account)
        break
      }
      default: {
        throw new Error(`Unknown value: ${account satisfies never}`)
      }
    }
  }

  return {
    evmChainAccountList: [...evmChainAccountsMap.entries()].map(
      ([chainId, accounts]) => ({
        chainId,
        accountList: accounts,
      }),
    ),
    exchangeCategoryAccountList: [...exchangeCategoryAccountsMap.entries()].map(
      ([category, accounts]) => ({
        category,
        accountList: accounts,
      }),
    ),
    polkadotChainAccountList: [...polkadotChainAccountsMap.entries()].map(
      ([chainId, accounts]) => ({
        chainId,
        accounts,
      }),
    ),
    solanaAccountList,
  }
}
